import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { can as canHelper, CanSections } from '../../helpers/can';

interface CanProps {
  section: CanSections;
  action: 'view' | 'create' | 'edit' | 'delete' | 'edit:own' | 'delete:own' | 'view:own';
}

interface ContexInitialValuesDto {
  can: ({ section, action }: CanProps) => boolean;
}

const contexInitialValues: ContexInitialValuesDto = {
  can: () => false,
};

// Define the context for access control
const AccessControlContext = createContext(contexInitialValues);

// Custom hook to use access control context
export const useAccessControl = () => useContext(AccessControlContext);

// Provider component for access control
const AccessControlProvider = ({
  currentUser,
  currentUserStates,
  children,
}) => {
  const profile = useMemo(() => currentUser?.profile, [currentUser]);
  const role = useMemo(() => currentUser?.roles[0], [currentUser]);

  // Function to check if user has access to a section
  const can = useCallback(
    ({ section, action }: CanProps) => {
      return canHelper({ profile, role, section, action });
    },
    [profile, role]
  );

  // Expose access control context value
  const contextValue = useMemo(
    () => ({
      can,
    }),
    [can]
  );

  return (
    <AccessControlContext.Provider value={contextValue}>
      {children}
    </AccessControlContext.Provider>
  );
};

const mapStateToProps = (state) => {
  const { data: currentUser, states: currentUserStates } = state.userStore.currentUser;
  return {
    currentUser,
    currentUserStates,
  };
};

export default connect(mapStateToProps)(React.memo(AccessControlProvider));
